// src/MathTools/ConfidenceIntervalTool.js
//-------------------------------------------------------------
import { useState } from "react";
const zCrit = { 0.9: 1.645, 0.95: 1.96, 0.99: 2.576 };

export default function ConfidenceIntervalTool({
  n = 25,        // sample size
  mean = 72,     // initial x̄
  s = 8,         // sample std-dev
  mu0 = 70,      // null-hypothesis value
  confidence = 0.95
}) {
  const [xBar, setXBar] = useState(mean);

  /* ---------- derived numbers ------------------------------------ */
  const se   = s / Math.sqrt(n);
  const z    = zCrit[confidence] ?? 1.96;
  const half = z * se;
  const ciLo = xBar - half;
  const ciHi = xBar + half;
  const reject = mu0 < ciLo || mu0 > ciHi;

  /* ---------- simple scale for the svg axis ---------------------- */
  const pad   = 4 * se;
  const minX  = Math.min(mu0, xBar) - pad;
  const maxX  = Math.max(mu0, xBar) + pad;
  const scale = x => 20 + ((x - minX) / (maxX - minX)) * 260;

  /* ---------- markup -------------------------------------------- */
  return (
    <div style={{ border: "1px solid #ccc", padding: 12, marginTop: 12, maxWidth: 400 }}>
      <h4>CI &amp; One-Sample Test (normal approx.)</h4>

      {/* fixed parameters summary */}
      <p style={{ fontSize: 20, margin: "4px 0 8px" }}>
        n = {n},&nbsp; s = {s},&nbsp; confidence = {confidence * 100}%,
        &nbsp; μ₀ = {mu0}
      </p>

      {/* slider only for sample mean */}
      <label style={{ fontSize: 24 }}>
        sample mean&nbsp;
        <input
        className="inputBox"
          type="range"
          min={minX} max={maxX} step={0.1}
          value={xBar}
          onChange={e => setXBar(+e.target.value)}
          style={{ width: 220, verticalAlign: "middle" }}
        />
        &nbsp;{xBar.toFixed(1)}
      </label>

      {/* CI whisker diagram */}
      <svg width={300} height={70} style={{ marginTop: 8 }}>
        {/* axis */}
        <line x1={20} x2={280} y1={35} y2={35} stroke="#888" />

        {/* CI bar */}
        <line x1={scale(ciLo)} x2={scale(ciHi)} y1={35} y2={35}
              stroke="#4D9DE0" strokeWidth={6}/>
        {/* whiskers */}
        <line x1={scale(ciLo)} x2={scale(ciLo)} y1={28} y2={42} stroke="#4D9DE0"/>
        <line x1={scale(ciHi)} x2={scale(ciHi)} y1={28} y2={42} stroke="#4D9DE0"/>

        {/* μ0 dashed line */}
        <line x1={scale(mu0)} x2={scale(mu0)} y1={20} y2={50}
              stroke="orange" strokeWidth={2} strokeDasharray="3,3"/>
        <text x={scale(mu0)} y={15} fontSize={10} textAnchor="middle">μ₀</text>
      </svg>

      <p style={{ fontSize: 20, margin: 0 }}>
        CI&nbsp;=&nbsp;({ciLo.toFixed(2)},&nbsp;{ciHi.toFixed(2)}) &nbsp;→&nbsp;
        {reject
          ? <span style={{ color: "crimson" }}>Reject&nbsp;H₀</span>
          : <span style={{ color: "green" }}>Fail&nbsp;to&nbsp;reject&nbsp;H₀</span>}
      </p>
    </div>
  );
}
